<template>
  <div class="social-float">
    <div class="rail">
      <a
        :href="`tel:${phoneHref}`"
        class="btn btn--phone"
        :aria-label="contact.phone"
      >
        <svg viewBox="0 0 24 24" class="icon"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.68 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.32 1.85.55 2.81.68A2 2 0 0 1 22 16.92z"/></svg>
        <span class="tip">{{ contact.phone }}</span>
      </a>

      <a
        v-for="c in channels"
        :key="c.key"
        :href="c.href"
        target="_blank"
        rel="noopener noreferrer"
        class="btn btn--channel"
        :style="{ '--brand': c.brand }"
        :aria-label="c.label"
      >
        <svg viewBox="0 0 24 24" class="icon" v-html="c.path" />
        <span class="tip">{{ c.label }}</span>
      </a>

      <button
        type="button"
        class="btn btn--top"
        :class="{ 'is-hidden': !showTop }"
        aria-label="Lên đầu trang"
        @click="scrollTop"
      >
        <svg viewBox="0 0 24 24" class="icon"><path d="M12 19V5M5 12l7-7 7 7"/></svg>
      </button>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import contact from '@/config/contact'

const ICONS = {
  zalo: '<path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>',
  messenger: '<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>',
  facebook: '<path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>',
  tiktok: '<path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/>',
  youtube: '<path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z"/><path d="m9.75 15.02 5.75-3.27-5.75-3.27v6.54z"/>'
}

const BRANDS = {
  zalo: '#0068ff',
  messenger: '#0084ff',
  facebook: '#1877f2',
  tiktok: '#111827',
  youtube: '#ff0000'
}

const LABELS = {
  zalo: 'Chat Zalo',
  messenger: 'Messenger',
  facebook: 'Facebook',
  tiktok: 'TikTok',
  youtube: 'YouTube'
}

export default {
  name: 'SocialFloat',
  setup () {
    const showTop = ref(false)
    const onScroll = () => { showTop.value = window.scrollY > 480 }

    onMounted(() => {
      onScroll()
      window.addEventListener('scroll', onScroll, { passive: true })
    })
    onUnmounted(() => window.removeEventListener('scroll', onScroll))

    const channels = computed(() =>
      Object.keys(ICONS)
        .filter((key) => contact[key])
        .map((key) => ({
          key,
          href: contact[key],
          label: LABELS[key],
          brand: BRANDS[key],
          path: ICONS[key]
        }))
    )

    return {
      contact,
      channels,
      showTop,
      phoneHref: computed(() => contact.phone.replace(/\s/g, '')),
      scrollTop: () => window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }
}
</script>

<style lang="scss" scoped>
.social-float {
  position: fixed;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  z-index: 40;
}

.rail {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.375rem;
  padding: 0.375rem;
  border-radius: 999px;
  border: 1px solid rgba(6, 78, 59, 0.12);
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(12px);
  box-shadow: 0 8px 24px -12px rgba(6, 78, 59, 0.45);
}

.btn {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 999px;
  border: 0;
  cursor: pointer;
  background: #f0f5f2;
  color: #4b6b5e;
  transition: background-color 0.2s, color 0.2s, filter 0.2s, opacity 0.2s, transform 0.2s;

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px #059669;
  }
}

.btn--phone {
  background: #059669;
  color: #fff;

  &:hover { filter: brightness(1.1); }
}

.btn--channel:hover {
  background: var(--brand);
  color: #fff;
}

.btn--top:hover {
  background: #059669;
  color: #fff;
}

.btn--top.is-hidden {
  opacity: 0;
  transform: scale(0.75);
  pointer-events: none;
}

.icon {
  width: 1rem;
  height: 1rem;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.tip {
  pointer-events: none;
  position: absolute;
  right: 100%;
  margin-right: 0.5rem;
  white-space: nowrap;
  border-radius: 0.375rem;
  background: #064e3b;
  color: #fff;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  opacity: 0;
  transition: opacity 0.2s;
}

.btn:hover .tip { opacity: 1; }

@media (max-width: 640px) {
  .tip { display: none; }
}

@media print {
  .social-float { display: none; }
}
</style>
